import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { palette } from "../palette";
import { fadeUp, revealProps, stagger, useReducedMotion } from "../motion";
import { Section } from "./Section";

export const FAQS = [
  {
    q: "What do I need to know before starting?",
    a: "Some coding. Not much, and not expertly — but you should be able to read a script and write a simple one without it being your first time. Ideally you are already working in IT or a data-adjacent role, because the transition is far shorter from there and you will have real problems to apply this to. If you are starting from genuinely zero, learn basic Python first and come back; we will tell you that honestly on the call rather than take your money and hope. What we do not require is a degree in mathematics, a computer science background, or any prior AI experience."
  },
  {
    q: "What does 'milestone-bound' actually mean?",
    a: "Your plan is organised around tangible milestones — master statistics and matrix calculus, train a neural network from scratch, deploy a production RAG system on AWS — rather than dates. You move to the next one when you have genuinely mastered the current one. Nothing expires, and a busy month at work costs you nothing."
  },
  {
    q: "Why do you strictly limit intake to 50 candidates at a time?",
    a: "We are practicing AI engineers, data scientists and startup founders, and we teach every session ourselves. Weekly 1:1 sessions, personalised assignments, line-by-line code review and provisioning cloud GPU infrastructure takes real time per person. Fifty is simply how many we can do that for properly."
  },
  {
    q: "Do you really provide AWS cloud compute credits and Google Colab Pro subscriptions?",
    a: "Yes. You cannot learn real AI engineering by reading slides or running toy notebooks on localhost. We provide AWS cloud compute credits and premium Google Colab subscriptions so you train models, run evaluations, optimize inference, and deploy production endpoints on actual cloud GPU hardware.",
  },
  {
    q: "Will I work with the same people throughout?",
    a: "Yes. The same small team designs your roadmap, writes your study material and assessments, reviews your code and teaches you one to one, from the first conversation to the last. Nobody gets reassigned, and you never have to explain your history again."
  },
  {
    q: "Can I focus exclusively on advanced specializations like LLM Internals, CUDA, or building my own AI product?",
    a: "Yes. Nobody is forced to take courses they don't need. If you already know software engineering and want to focus purely on LLM internals, attention mechanisms, CUDA/Triton kernel programming, or AI system design (HLD & LLD), or if you are a founder architecting an AI startup, we build your milestone plan around that exact aspiration.",
  },
];

export const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const reduced = useReducedMotion();

  const toggle = (idx) => {
    setOpenIndex((prev) => (prev === idx ? null : idx));
  };

  return (
    <Section
      id="faq"
      align="center"
      eyebrow="Frequently Asked Questions"
      title="Clear answers to your honest questions"
      lead="Everything you need to know about our milestone-bound learning, 1:1 mentorship, compute access, and cohort limit."
      maxWidth="md"
    >
      <motion.div variants={stagger(0.06)} {...revealProps(reduced)}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, textAlign: "left" }}>
          {FAQS.map((item, idx) => {
            const isOpen = openIndex === idx;

            return (
              <motion.div variants={fadeUp} key={item.q}>
                <Box
                  onClick={() => toggle(idx)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggle(idx);
                    }
                  }}
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: "18px",
                    backgroundColor: palette.paper,
                    border: `1px solid ${isOpen ? palette.primaryBorder : "rgba(22, 24, 29, 0.08)"}`,
                    boxShadow: isOpen ? palette.shadowMd : palette.shadowSm,
                    cursor: "pointer",
                    transition: "all 0.25s cubic-bezier(0.22, 0.61, 0.36, 1)",
                    "&:hover": {
                      borderColor: palette.primaryBorder,
                      backgroundColor: isOpen ? palette.paper : palette.bgAlt,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Typography
                      component="h3"
                      sx={{
                        fontSize: { xs: 16, sm: 17.5 },
                        fontWeight: 700,
                        color: palette.text,
                        lineHeight: 1.4,
                      }}
                    >
                      {item.q}
                    </Typography>
                    <Box
                      component="span"
                      aria-hidden="true"
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        backgroundColor: isOpen ? palette.primary : palette.bgAlt,
                        color: isOpen ? "#ffffff" : palette.textMuted,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        fontSize: 16,
                        fontWeight: 700,
                        transition: "all 0.2s ease",
                      }}
                    >
                      {isOpen ? "−" : "+"}
                    </Box>
                  </Box>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                      >
                        <Box sx={{ pt: 2, mt: 1.5, borderTop: `1px solid rgba(22, 24, 29, 0.06)` }}>
                          <Typography
                            component="p"
                            sx={{
                              color: palette.textMuted,
                              fontSize: { xs: 14.5, sm: 15.5 },
                              lineHeight: 1.68,
                            }}
                          >
                            {item.a}
                          </Typography>
                        </Box>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Box>
              </motion.div>
            );
          })}
        </Box>
      </motion.div>
    </Section>
  );
};

export default FAQ;
